import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';

interface ConverseCall {
  id: string; callId: string; asteriskUuid?: string; callerNumber?: string; channel?: string;
  status?: string; language?: string; startedAt?: string; endedAt?: string;
  turnCount?: number; audioTurnCount?: number; publicationId?: string; snapshotHash?: string;
  fullCallAudioAvailable?: boolean; fullCallAudioBytes?: number;
}

interface ConverseTurn {
  turnId: string; turnNo: number; status?: string; transcript?: string; replyText?: string;
  responsePurpose?: string; capability?: string; action?: string; turnLanguage?: string;
  responseLanguage?: string; languageConfidence?: number; asrQuality?: Record<string, unknown>;
  failureCode?: string; createdAt?: string; callerAudioAvailable?: boolean; replyAudioAvailable?: boolean;
}

type SortKey = 'startedAt' | 'callerNumber' | 'language' | 'turnCount' | 'status';
const API_URL = environment.apiHost;

@Component({
  selector: 'app-ivr-submissions', standalone: true, imports: [CommonModule, FormsModule],
  templateUrl: './ivr-submissions.component.html', styleUrls: ['./ivr-submissions.component.scss'],
})
export class IvrSubmissionsComponent implements OnInit, OnDestroy {
  calls: ConverseCall[] = [];
  turns: ConverseTurn[] = [];
  activeCall: ConverseCall | null = null;
  loading = false;
  detailsLoading = false;
  error = '';
  detailsError = '';
  query = '';
  languageFilter = 'all';
  statusFilter = 'all';
  sortBy: SortKey = 'startedAt';
  sortDir: 'asc' | 'desc' = 'desc';
  private readonly audioUrls = new Map<string, string>();
  private readonly audioLoading = new Set<string>();

  constructor(private readonly router: Router) {}
  ngOnInit(): void { void this.load(); }
  ngOnDestroy(): void { this.audioUrls.forEach(url => URL.revokeObjectURL(url)); this.audioUrls.clear(); }

  async load(): Promise<void> {
    this.loading = true; this.error = '';
    try {
      const response = await fetch(`${API_URL}/api/v1/telephony/ivr/interactions?limit=1000`, { headers: this.authHeaders() });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.message || `HTTP ${response.status}`);
      const records = payload?.data?.records ?? [];
      this.calls = Array.isArray(records) ? records : [];
    } catch (error: any) {
      this.calls = []; this.error = error?.message || 'Unified SABA call history could not be loaded.';
    } finally { this.loading = false; }
  }

  get filtered(): ConverseCall[] {
    const query = this.query.trim().toLowerCase();
    return this.calls.filter(call => {
      if (this.languageFilter !== 'all' && this.languageKey(call.language) !== this.languageFilter) return false;
      if (this.statusFilter !== 'all' && String(call.status || '').toUpperCase() !== this.statusFilter) return false;
      if (!query) return true;
      return [call.callerNumber, call.callId, call.asteriskUuid, call.status, call.language]
        .some(value => String(value || '').toLowerCase().includes(query));
    }).sort((left, right) => {
      const value = (call: ConverseCall): string | number => {
        if (this.sortBy === 'startedAt') return new Date(call.startedAt || 0).getTime();
        if (this.sortBy === 'turnCount') return call.turnCount || 0;
        return String(call[this.sortBy] || '').toLowerCase();
      };
      const a = value(left); const b = value(right); const result = a > b ? 1 : a < b ? -1 : 0;
      return this.sortDir === 'asc' ? result : -result;
    });
  }

  get totalTurns(): number { return this.calls.reduce((sum, call) => sum + (call.turnCount || 0), 0); }
  get englishCalls(): number { return this.calls.filter(call => this.languageKey(call.language) === 'en').length; }
  get urduCalls(): number { return this.calls.filter(call => this.languageKey(call.language) === 'ur').length; }
  get recordedCalls(): number { return this.calls.filter(call => !!call.fullCallAudioAvailable).length; }

  clearFilters(): void { this.query = ''; this.languageFilter = 'all'; this.statusFilter = 'all'; }
  sort(key: SortKey): void {
    if (this.sortBy === key) this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    else { this.sortBy = key; this.sortDir = key === 'startedAt' ? 'desc' : 'asc'; }
  }
  sortIcon(key: SortKey): string { return this.sortBy !== key ? 'unfold_more' : this.sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'; }

  async openCall(call: ConverseCall, loadFullAudio = false): Promise<void> {
    this.activeCall = call; this.turns = []; this.detailsError = ''; this.detailsLoading = true;
    if (loadFullAudio && call.fullCallAudioAvailable) void this.loadFullCallAudio(call);
    try {
      const response = await fetch(`${API_URL}/api/v1/telephony/ivr/interactions/${call.callId}/turns`, { headers: this.authHeaders() });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.message || `HTTP ${response.status}`);
      const records = payload?.data?.records ?? [];
      this.turns = Array.isArray(records) ? records : [];
    } catch (error: any) { this.detailsError = error?.message || 'The call transcript could not be loaded.'; }
    finally { this.detailsLoading = false; }
  }

  closeCall(): void { this.activeCall = null; this.turns = []; this.detailsError = ''; }

  async loadFullCallAudio(call: ConverseCall): Promise<void> {
    const key = this.fullCallAudioKey(call.callId);
    if (this.audioUrls.has(key) || this.audioLoading.has(key)) return;
    this.audioLoading.add(key);
    try {
      const response = await fetch(`${API_URL}/api/v1/telephony/ivr/interactions/${call.callId}/audio`, { headers: this.authHeaders() });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      this.audioUrls.set(key, URL.createObjectURL(await response.blob()));
    } catch (error: any) {
      this.detailsError = error?.message || 'The full call recording could not be loaded.';
    } finally { this.audioLoading.delete(key); }
  }

  fullCallAudioUrl(call: ConverseCall): string | null { return this.audioUrls.get(this.fullCallAudioKey(call.callId)) || null; }
  isFullCallAudioLoading(call: ConverseCall): boolean { return this.audioLoading.has(this.fullCallAudioKey(call.callId)); }

  async loadAudio(turn: ConverseTurn, side: 'caller' | 'saba'): Promise<void> {
    if (!this.activeCall) return;
    const key = this.audioKey(this.activeCall.callId, turn.turnNo, side);
    if (this.audioUrls.has(key) || this.audioLoading.has(key)) return;
    this.audioLoading.add(key);
    try {
      const response = await fetch(`${API_URL}/api/v1/telephony/ivr/interactions/${this.activeCall.callId}/turns/${turn.turnNo}/audio/${side}`, { headers: this.authHeaders() });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      this.audioUrls.set(key, URL.createObjectURL(await response.blob()));
    } finally { this.audioLoading.delete(key); }
  }

  audioUrl(turn: ConverseTurn, side: 'caller' | 'saba'): string | null {
    return this.activeCall ? this.audioUrls.get(this.audioKey(this.activeCall.callId, turn.turnNo, side)) || null : null;
  }
  isAudioLoading(turn: ConverseTurn, side: 'caller' | 'saba'): boolean {
    return !!this.activeCall && this.audioLoading.has(this.audioKey(this.activeCall.callId, turn.turnNo, side));
  }

  goToConsole(): void { void this.router.navigate(['/telephony']); }
  languageLabel(language?: string): string {
    const key = this.languageKey(language); return key === 'ur' ? 'Urdu' : key === 'en' ? 'English' : 'Not established';
  }
  duration(call: ConverseCall): string {
    if (!call.startedAt || !call.endedAt) return call.status === 'ACTIVE' ? 'In progress' : '—';
    const seconds = Math.max(0, Math.round((new Date(call.endedAt).getTime() - new Date(call.startedAt).getTime()) / 1000));
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  }
  statusLabel(value?: string): string {
    return String(value || 'UNKNOWN').replaceAll('_', ' ').toLowerCase().replace(/\b\w/g, letter => letter.toUpperCase());
  }
  private languageKey(language?: string): string {
    const value = String(language || '').toLowerCase();
    if (value.startsWith('urdu') || value === 'ur') return 'ur';
    if (value.startsWith('english') || value === 'en') return 'en';
    return 'unknown';
  }
  private audioKey(callId: string, revision: number, side: string): string { return `${callId}:${revision}:${side}`; }
  private fullCallAudioKey(callId: string): string { return `${callId}:full-call`; }
  private authHeaders(): HeadersInit {
    const token = localStorage.getItem('auth-token') ?? '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
}
